import { yupResolver } from '@hookform/resolvers/yup';
import { CountryCode } from 'libphonenumber-js';
import { useEffect } from 'react';
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import {
  useAddCrewMember,
  useAddWorker,
  useEditCrewMember,
  useEditWorker,
} from '@/modules/workforce/Crew/apiHooks';
import useCrewFormHook from '@/modules/workforce/Crew/components/AddEditForm/logic';
import { AddEditWorker } from '@/modules/workforce/Crew/components/Modals/AddEditWorker';
import { AddingCrewMemberSuccessModal } from '@/modules/workforce/Crew/components/Modals/AddingCrewMemberSuccessModal';
import FacialRecognition from '@/modules/workforce/Crew/components/Workers/FacialRecognition';
import {
  AddCrewMemberResponse,
  AddWorkerMemberResponse,
  IAddCrewForm,
  IAddWorkerForm,
} from '@/modules/workforce/Crew/types';
import {
  Button,
  Collapse,
  InputField,
  PhoneInputWithCountry,
  Typography,
} from '@/shared/components/atoms';
import { ImagePickerNewD } from '@/shared/components/atoms/ImagePickerNewD';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

// TODO: use inteface with condition check for crewType
const AddEditForm: React.FC<{
  crewType: 'crew' | 'worker';
  memberId?: number;
  supplierId?: number;
  subTitle?: string;
  initialValues?: IAddCrewForm | IAddWorkerForm;
  onSuccess?: () => void;
}> = ({
  crewType,
  subTitle,
  initialValues,
  memberId,
  supplierId,
  onSuccess,
}) => {
  const { t } = useTranslation('common');
  const { openModal, closeModal } = useModal();
  const { user } = useAuth();
  const [selectedAsset] = useStore((store) => store?.asset);
  const { workerValidationSchema, crewValidationSchema } = useCrewFormHook();
  const { openCreateContractModal } = useCreateEditContractUtils();
  // TODO: update IAddWorkerForm, workerValidationSchema, crewValidationSchema

  // Initialize mutation hooks
  const { mutate: addCrewMutation } = useAddCrewMember();
  const { mutate: addWorkerMutation } = useAddWorker();
  const { mutate: editCrewMutation } = useEditCrewMember();
  const { mutate: editWorkerMutation } = useEditWorker();

  type FormTypes = typeof crewType extends 'crew'
    ? IAddCrewForm
    : IAddWorkerForm;

  const formMethods = useForm<FormTypes>({
    resolver: yupResolver(
      crewType === 'worker'
        ? workerValidationSchema(t, user?.company?.isFacialRecognitionActive)
        : crewValidationSchema(t)
    ),
    mode: 'all',
    defaultValues: {
      ...initialValues,
      countryCode: initialValues?.countryCode ?? selectedAsset?.country ?? 'EG',
    },
  });

  const {
    register,
    formState: { errors, isValid, isValidating, isSubmitting },
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    control,
  } = formMethods;
  const profilePictureWatcher = watch('profilePicture');

  const onSubmit = (values: FormTypes) => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value) && value[0]?.file instanceof File) {
          formData.append(key, value[0].file);
        } else {
          formData.append(key, String(value));
        }
      }
      //  else {
      //   formData.append(key, '');
      // }
    });

    // Handle form submission
    if (crewType === 'crew') {
      if (selectedAsset) {
        formData.append('entityId', selectedAsset.id.toString());
      }
      if (initialValues && memberId) {
        editCrewMutation(
          {
            crewId: memberId,
            memberDetails: formData,
          },
          {
            onSuccess: () => {
              toast.success(t('changes-saved'));
              closeModal();
            },
          }
        );
      } else {
        addCrewMutation(formData, {
          onSuccess: (response: AddCrewMemberResponse) => {
            openModal(
              <AddingCrewMemberSuccessModal
                crewType={crewType}
                actionHandlerFunction={() => {
                  closeModal();
                  closeModal();
                  openModal(
                    <AddEditWorker
                      onSuccess={onSuccess}
                      supplierId={response.supplierId}
                      supplierName={response?.supplierName ?? ''}
                      initialValues={undefined}
                    />,
                    {
                      variant: 'full',
                      title: t('add-worker'),
                      titleVariant: 'h6',
                      onClose: () =>
                        openModal(
                          <ConfirmationModal
                            header={t('going-somewhere-else')}
                            message={t('you-will-lose-all-data')}
                            variant='warning'
                            dismissText={t('continue-editing')}
                            confirmText={t('discard-and-exit')}
                            onDismissRemoveNumber={1}
                            onConfirm={closeModal}
                            buttonsAlignment='col'
                            preferredAction='dismiss'
                          />
                        ),
                    }
                  );
                }}
                addAnotherHandlerFunction={() => {
                  closeModal();
                  reset();
                }}
              />,
              {
                containerClassName: 'max-w-md',
                onClose: () => {
                  closeModal();
                  closeModal();
                  onSuccess?.();
                },
              }
            );
          },
        });
      }
    } else {
      if (supplierId) {
        formData.append('supplierId', supplierId.toString());
      }
      if (initialValues && memberId) {
        editWorkerMutation(
          {
            workerId: memberId,
            memberDetails: formData,
          },
          {
            onSuccess: () => {
              toast.success(t('changes-saved'));
              closeModal();
              reset();
            },
          }
        );
      } else {
        addWorkerMutation(formData, {
          onSuccess: (response: AddWorkerMemberResponse) => {
            openModal(
              <AddingCrewMemberSuccessModal
                crewType={crewType}
                actionHandlerFunction={() => {
                  closeModal(2);
                  openCreateContractModal({
                    name: response.supplierName,
                    id: response.supplierId,
                  });
                }}
                addAnotherHandlerFunction={() => {
                  closeModal();
                  reset();
                  setValue('profilePicture', undefined);
                }}
              />,
              {
                containerClassName: 'max-w-md',
                onClose: () => {
                  closeModal();
                  closeModal();
                  onSuccess?.();
                },
              }
            );
          },
        });
      }
    }
  };

  useEffect(() => {
    if (initialValues) {
      reset({
        ...initialValues,
        countryCode:
          initialValues?.countryCode ?? selectedAsset?.country ?? 'EG',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, selectedAsset]);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='mx-auto flex h-full max-w-xl flex-col justify-between gap-2 pt-32 pb-3'
      >
        <div>
          <div className='mb-24'>
            <Typography
              variant='h2'
              weight='semiBold'
              className='pb-0 capitalize'
            >
              {t(
                initialValues
                  ? crewType === 'crew'
                    ? `edit-crew`
                    : 'edit-worker'
                  : crewType === 'crew'
                  ? `add-crew`
                  : 'add-worker'
              )}
            </Typography>

            <Typography
              variant='body1'
              weight='regular'
              className='text-surface-10'
            >
              {subTitle && crewType === 'worker'
                ? `${t('crew')}: ${subTitle}`
                : initialValues
                ? ''
                : t('add-crew-description')}
            </Typography>
          </div>
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-16'
            label={crewType === 'crew' ? t('crew-name') : t('worker-name')}
            placeholder={
              crewType === 'crew'
                ? t('crew-name-placeholder')
                : t('worker-name-placeholder')
            }
            error={!!errors.name}
            errorMessage={errors.name?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('name')}
          />
          {crewType === 'worker' && user?.company?.isFacialRecognitionActive ? (
            <FacialRecognition />
          ) : (
            <></>
          )}
          <Collapse
            containerClassName='border-t-sm border-neutralVariant-90 mt-24 pt-16 rounded-[0px]'
            childrenClassName='flex flex-col gap-2'
            itemClassName='!justify-start gap-2'
            header={
              <Typography weight='semiBold'>
                {t('additional-information')} ( {t('optional')} )
              </Typography>
            }
          >
            {/* TODO: check types error */}
            <PhoneInputWithCountry
              label={`${t('phone-number')}  (${t('optional')})`}
              width='w-full'
              id='phoneNumber'
              defaultCountry={(getValues('countryCode') || 'EG') as CountryCode}
              name='phoneNumber'
              control={control as unknown as Control<FieldValues>}
              placeholder={t('enter-mobile-number')}
              errorMessage={errors?.phoneNumber?.message as string}
              className='rounded-xl bg-background-transparent py-2'
              borderColor='border-outline-variant border-xs'
            />
            <div className='mt-16 flex flex-col gap-16 border-t-sm border-neutralVariant-90 pt-24'>
              <Typography variant='h3' weight='semiBold' className='capitalize'>
                {t('id-photo')} ({t('optional')})
              </Typography>
              <div>
                {/* TODO: update image picker */}
                <Typography>{t('front-side')}</Typography>
                {/* TODO: khalid - update name! */}
                <ImagePickerNewD fullWidth id='nationalIdFront' />
              </div>
              <div>
                <Typography>{t('back-side')}</Typography>
                <ImagePickerNewD fullWidth id='nationalIdBack' />
              </div>
            </div>
            {crewType === 'worker' ? (
              <>
                <InputField
                  borderColor='border-outline-variant'
                  width='w-full'
                  py='!py-16'
                  label={t('id-number')}
                  placeholder={t('id-number')}
                  error={!!errors.nationalIdNumber}
                  errorMessage={errors.nationalIdNumber?.message}
                  className='!rounded-xl bg-background-transparent'
                  {...register('nationalIdNumber')}
                />
                <InputField
                  borderColor='border-outline-variant'
                  width='w-full'
                  py='!py-16'
                  label={t('company-code')}
                  placeholder={t('company-code')}
                  error={!!errors.customId}
                  errorMessage={errors.customId?.message}
                  className='!rounded-xl bg-background-transparent'
                  {...register('customId')}
                />
              </>
            ) : (
              <></>
            )}
          </Collapse>
        </div>
        <Button
          size='full'
          type='submit'
          className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
          disabled={
            !isValid ||
            isValidating ||
            isSubmitting ||
            Object.keys(errors).length > 0 ||
            (crewType === 'worker' &&
              user?.company?.isFacialRecognitionActive &&
              !profilePictureWatcher)
          }
        >
          {t(
            initialValues
              ? 'save-edits'
              : crewType === 'crew'
              ? `add-crew`
              : 'add-worker'
          )}
        </Button>
      </form>
    </FormProvider>
  );
};

export { AddEditForm };
